type PayloadMediaDownloadUrlOptions = {
    url?: string | null
    answerId?: string | number | null
    attachmentId?: string | number | null
    blobId?: string | number | null
    apiBaseUrl?: string | null
}

const ANSWER_MEDIA_PATH_RE = /^\/api\/v1\/answers\/([^/]+)\/media\/?$/
const FALLBACK_URL_BASE = 'http://payload.local'

function presentString(value: unknown): string | null {
    if (value == null) return null
    const stringValue = String(value).trim()
    return stringValue.length ? stringValue : null
}

function currentApiBaseUrl(): string {
    if (typeof window === 'undefined') return ''

    const rawApiUrl = presentString((window as any).knovApiUrl)
    return rawApiUrl ? rawApiUrl.replace(/\/+$/, '') : ''
}

function parsePayloadUrl(url?: string | null): URL | null {
    const rawUrl = presentString(url)
    if (!rawUrl) return null

    try {
        return new URL(rawUrl, FALLBACK_URL_BASE)
    } catch {
        return null
    }
}

function stableUrlShape(parsedUrl: URL, rawUrl: string): string {
    const pathWithQuery = `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
    if (rawUrl.startsWith('//')) return `//${parsedUrl.host}${pathWithQuery}`
    if (/^https?:\/\//i.test(rawUrl)) return parsedUrl.toString()
    return pathWithQuery
}

function isStableAnswerMediaUrl(parsedUrl: URL | null): boolean {
    return Boolean(parsedUrl && ANSWER_MEDIA_PATH_RE.test(parsedUrl.pathname))
}

export function buildPayloadMediaDownloadUrl({
    url,
    answerId,
    attachmentId,
    blobId,
    apiBaseUrl,
}: PayloadMediaDownloadUrlOptions): string | null {
    const rawUrl = presentString(url)
    const parsedUrl = parsePayloadUrl(rawUrl)
    const stableMediaUrl = isStableAnswerMediaUrl(parsedUrl)
    const stablePreviewMediaUrl = Boolean(
        stableMediaUrl && parsedUrl?.searchParams.has('preview_key'),
    )
    const stableAnswerId =
        stableMediaUrl && parsedUrl
            ? decodeURIComponent(parsedUrl.pathname.match(ANSWER_MEDIA_PATH_RE)?.[1] || '')
            : null
    const resolvedAnswerId = presentString(answerId) || stableAnswerId
    const explicitBlobId = presentString(blobId)
    const resolvedAttachmentId =
        presentString(attachmentId) || parsedUrl?.searchParams.get('attachment_id')
    const resolvedBlobId =
        explicitBlobId || (stablePreviewMediaUrl ? null : parsedUrl?.searchParams.get('blob_id'))

    if (!resolvedAnswerId || (!resolvedAttachmentId && !resolvedBlobId)) return rawUrl

    if (stableMediaUrl && parsedUrl && rawUrl) {
        if (resolvedAttachmentId) parsedUrl.searchParams.set('attachment_id', resolvedAttachmentId)
        if (resolvedBlobId) {
            parsedUrl.searchParams.set('blob_id', resolvedBlobId)
        } else {
            parsedUrl.searchParams.delete('blob_id')
        }
        parsedUrl.searchParams.delete('preview_key')
        parsedUrl.searchParams.set('size', 'original')
        parsedUrl.searchParams.set('force_nocdn', 'true')
        parsedUrl.searchParams.set('disposition', 'attachment')
        return stableUrlShape(parsedUrl, rawUrl)
    }

    const baseUrl = presentString(apiBaseUrl)?.replace(/\/+$/, '') || currentApiBaseUrl()
    const params = new URLSearchParams()
    if (resolvedAttachmentId) params.set('attachment_id', resolvedAttachmentId)
    if (resolvedBlobId) params.set('blob_id', resolvedBlobId)
    params.set('size', 'original')
    const token = parsedUrl?.searchParams.get('vtoken')
    if (token) params.set('vtoken', token)
    params.set('force_nocdn', 'true')
    params.set('disposition', 'attachment')

    return `${baseUrl}/api/v1/answers/${encodeURIComponent(
        resolvedAnswerId,
    )}/media?${params.toString()}`
}
